import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineArrowLeft,
  HiOutlineBolt,
  HiOutlineClock,
  HiOutlineChartBar,
  HiOutlineTrophy,
  HiOutlineCalendarDays,
  HiOutlineClipboardDocumentList,
  HiOutlineArrowPath,
  HiOutlineUserCircle,
  HiOutlineBuildingOffice2,
  HiOutlineIdentification
} from "react-icons/hi2";
import { Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#CCFBF1",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#0EA5E9"
};

const DAILY_TARGET = 40;

function formatISODate(date) {
  return date.toISOString().split("T")[0];
}

function EmployeeDetails({ userId, setPage }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [fromDate, setFromDate] = useState(formatISODate(firstDayOfMonth));
  const [toDate, setToDate] = useState(formatISODate(today));

  useEffect(() => {
    if (userId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/production/user/${userId}`);
      setAllData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Employee details load failed:", err);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  const resetDateRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(formatISODate(start));
    setToDate(formatISODate(now));
  };

  const data = useMemo(() => {
    if (!fromDate || !toDate) return allData;

    const start = new Date(fromDate);
    const end = new Date(toDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return allData.filter((row) => {
      const d = new Date(row.date);
      return d >= start && d <= end;
    });
  }, [allData, fromDate, toDate]);

  const employeeName = allData[0]?.user || "Employee";
  const employeePractice = allData[0]?.practice || "-";
  const recordCount = data.length;

  const totalAchieved = useMemo(
    () => data.reduce((sum, row) => sum + Number(row.production_count || 0), 0),
    [data]
  );

  const totalHours = useMemo(
    () => data.reduce((sum, row) => sum + Number(row.hours_contributed || 0), 0),
    [data]
  );

  const uniqueDates = useMemo(
    () => [...new Set(data.map((row) => row.date))],
    [data]
  );

  const totalTarget = uniqueDates.length * DAILY_TARGET;

  const productivity = totalTarget
    ? ((totalAchieved / totalTarget) * 100).toFixed(1)
    : "0.0";

  const taskMap = useMemo(() => {
    const map = {};

    data.forEach((row) => {
      const task = row.task || "Unknown";

      if (!map[task]) {
        map[task] = {
          achieved: 0,
          hours: 0
        };
      }

      map[task].achieved += Number(row.production_count || 0);
      map[task].hours += Number(row.hours_contributed || 0);
    });

    return map;
  }, [data]);

  const bestTask = useMemo(() => {
    const tasks = Object.keys(taskMap);
    if (!tasks.length) return "-";

    return tasks.reduce((a, b) =>
      taskMap[a].achieved > taskMap[b].achieved ? a : b
    );
  }, [taskMap]);

  const taskLabels = Object.keys(taskMap);
  const taskValues = taskLabels.map((task) => taskMap[task].achieved);

  const pieData = {
    labels: taskLabels,
    datasets: [
      {
        label: "Task Contribution",
        data: taskValues,
        backgroundColor: [
          "#0F766E",
          "#14B8A6",
          "#2DD4BF",
          "#0EA5E9",
          "#F59E0B",
          "#16A34A",
          "#8B5CF6",
          "#F43F5E"
        ],
        borderColor: "#FFFFFF",
        borderWidth: 2
      }
    ]
  };

  const groupedByDate = useMemo(() => {
    const map = {};

    data.forEach((row) => {
      const key = row.date;
      if (!map[key]) {
        map[key] = 0;
      }
      map[key] += Number(row.production_count || 0);
    });

    return Object.entries(map)
      .map(([date, achieved]) => ({
        date,
        achieved,
        target: DAILY_TARGET
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const lineData = {
    labels: groupedByDate.map((row) =>
      new Date(row.date).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short"
      })
    ),
    datasets: [
      {
        label: "Achieved",
        data: groupedByDate.map((row) => row.achieved),
        borderColor: "#0F766E",
        backgroundColor: "rgba(15,118,110,0.10)",
        tension: 0.35,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 5,
        pointBackgroundColor: "#0F766E",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2
      },
      {
        label: "Target",
        data: groupedByDate.map((row) => row.target),
        borderColor: "#F59E0B",
        backgroundColor: "rgba(245,158,11,0.08)",
        tension: 0.35,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: "#F59E0B",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2
      }
    ]
  };

  const chartHasData = data.length > 0 && groupedByDate.length > 0;
  const pieHasData = taskLabels.length > 0;

  return (
    <div style={page}>
      <button onClick={() => setPage("manager")} style={backBtn}>
        <HiOutlineArrowLeft style={{ fontSize: 16 }} />
        Back to Team Overview
      </button>

      <div style={hero}>
        <div>
          <h2 style={title}>{employeeName}</h2>
          <p style={subtitle}>
            Employee-wise performance insights, task contribution, and production records.
          </p>
        </div>

        <div style={heroRight}>
          <div style={controlsRow}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={dateInput}
            />

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={dateInput}
            />

            <button type="button" onClick={resetDateRange} style={resetBtn}>
              Reset
            </button>

            <button type="button" onClick={loadData} style={refreshBtn}>
              <HiOutlineArrowPath style={{ fontSize: 14 }} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div style={profileStrip}>
        <div style={profileLeft}>
          <div style={profileAvatar}>
            <HiOutlineUserCircle />
          </div>
          <div>
            <div style={profileName}>{employeeName}</div>
            <div style={profileMeta}>Focused employee performance view</div>
          </div>
        </div>

        <div style={profileFacts}>
          <div style={factChip}>
            <HiOutlineBuildingOffice2 style={{ fontSize: 14 }} />
            <span>Practice: {employeePractice}</span>
          </div>

          <div style={factChip}>
            <HiOutlineIdentification style={{ fontSize: 14 }} />
            <span>Records: {recordCount}</span>
          </div>

          <div style={factChip}>
            <HiOutlineChartBar style={{ fontSize: 14 }} />
            <span>Productivity: {productivity}%</span>
          </div>
        </div>
      </div>

      <div style={summaryGrid}>
        <div style={summaryCard}>
          <div style={iconBox}>
            <HiOutlineBolt />
          </div>
          <p style={cardLabel}>Total Achieved</p>
          <h3 style={cardValue}>{totalAchieved}</h3>
        </div>

        <div style={summaryCard}>
          <div style={iconBox}>
            <HiOutlineClock />
          </div>
          <p style={cardLabel}>Total Hours</p>
          <h3 style={cardValue}>{Number(totalHours).toFixed(1)}</h3>
        </div>

        <div style={summaryCard}>
          <div style={iconBox}>
            <HiOutlineChartBar />
          </div>
          <p style={cardLabel}>Productivity</p>
          <h3 style={cardValue}>{productivity}%</h3>
        </div>

        <div style={summaryCard}>
          <div style={iconBox}>
            <HiOutlineTrophy />
          </div>
          <p style={cardLabel}>Best Task</p>
          <h3 style={cardValueSmall}>{bestTask}</h3>
        </div>
      </div>

      <div style={chartsGrid}>
        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineClipboardDocumentList />
              </div>
              <div>
                <h3 style={panelTitle}>Task Contribution</h3>
                <p style={panelSubtitle}>Task-wise production share</p>
              </div>
            </div>
          </div>

          <div style={chartArea}>
            {loading ? (
              <div style={emptyState}>Loading chart...</div>
            ) : pieHasData ? (
              <Doughnut
                data={pieData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: "66%",
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        color: theme.textMuted,
                        padding: 12,
                        boxWidth: 10,
                        font: { size: 11 }
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const label = context.label || "";
                          const value = context.raw || 0;
                          const hours = taskMap[label]?.hours || 0;
                          return `${label}: ${value} achieved | Hours: ${hours}`;
                        }
                      }
                    }
                  }
                }}
              />
            ) : (
              <div style={emptyState}>No task contribution data available</div>
            )}
          </div>
        </div>

        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineCalendarDays />
              </div>
              <div>
                <h3 style={panelTitle}>Target vs Achieved</h3>
                <p style={panelSubtitle}>Performance trend by work date</p>
              </div>
            </div>
          </div>

          <div style={chartArea}>
            {loading ? (
              <div style={emptyState}>Loading chart...</div>
            ) : chartHasData ? (
              <Line
                data={lineData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                      labels: {
                        color: theme.textMuted,
                        font: { size: 11 }
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: {
                        color: theme.textMuted,
                        font: { size: 11 }
                      }
                    },
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: theme.textMuted,
                        font: { size: 11 }
                      },
                      grid: { color: "#E2E8F0" }
                    }
                  }
                }}
              />
            ) : (
              <div style={emptyState}>No target vs achieved data available</div>
            )}
          </div>
        </div>
      </div>

      <div style={tablePanel}>
        <div style={tableHeader}>
          <h3 style={tableTitle}>Production Records</h3>
          <p style={tableSubtitle}>All available entries for this employee</p>
        </div>

        <div style={tableWrapOuter}>
          <table style={table}>
            <thead>
              <tr style={tableHeadRow}>
                <th style={th}>Date</th>
                <th style={th}>Practice</th>
                <th style={th}>Task</th>
                <th style={th}>Achieved</th>
                <th style={th}>Hours</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                    Loading...
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                    No data found
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={row.id}
                    style={{
                      background: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC"
                    }}
                  >
                    <td style={td}>{new Date(row.date).toLocaleDateString()}</td>
                    <td style={td}>{row.practice}</td>
                    <td style={td}>{row.task}</td>
                    <td style={td}>{row.production_count}</td>
                    <td style={td}>{row.hours_contributed || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif",
  color: theme.text,
  padding: "10px 6px"
};

const backBtn = {
  border: "none",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  padding: "10px 14px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "14px",
  fontSize: "12px"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "10px",
  marginBottom: "14px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 700,
  color: theme.text
};

const subtitle = {
  margin: "4px 0 0 0",
  color: theme.textMuted,
  fontSize: "12px",
  lineHeight: 1.5
};

const heroRight = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "10px"
};

const controlsRow = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const dateInput = {
  height: "34px",
  borderRadius: "10px",
  border: "1px solid #D1D5DB",
  padding: "0 10px",
  fontSize: "12px",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
  minWidth: "152px"
};

const resetBtn = {
  border: "none",
  borderRadius: "10px",
  padding: "8px 12px",
  background: "#E2E8F0",
  color: theme.text,
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer"
};

const refreshBtn = {
  border: "none",
  borderRadius: "10px",
  padding: "8px 12px",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(15,118,110,0.16)"
};

const profileStrip = {
  background: theme.surface,
  borderRadius: "16px",
  padding: "14px 16px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
  marginBottom: "16px",
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  flexWrap: "wrap",
  alignItems: "center"
};

const profileLeft = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const profileAvatar = {
  width: "42px",
  height: "42px",
  borderRadius: "14px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px"
};

const profileName = {
  fontSize: "15px",
  fontWeight: 800,
  color: theme.text
};

const profileMeta = {
  marginTop: "4px",
  fontSize: "12px",
  color: theme.textMuted
};

const profileFacts = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center"
};

const factChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: "999px",
  padding: "8px 10px",
  fontSize: "12px",
  color: theme.text
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "14px",
  marginBottom: "16px"
};

const summaryCard = {
  background: theme.surface,
  borderRadius: "16px",
  padding: "16px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)"
};

const iconBox = {
  width: "36px",
  height: "36px",
  borderRadius: "10px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  marginBottom: "10px"
};

const cardLabel = {
  margin: 0,
  fontSize: "12px",
  color: theme.textMuted,
  fontWeight: 600
};

const cardValue = {
  margin: "8px 0 0 0",
  fontSize: "22px",
  fontWeight: 800,
  color: theme.text
};

const cardValueSmall = {
  margin: "8px 0 0 0",
  fontSize: "16px",
  fontWeight: 800,
  color: theme.text,
  lineHeight: 1.4,
  wordBreak: "break-word"
};

const chartsGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "14px",
  marginBottom: "16px"
};

const panel = {
  background: theme.surface,
  borderRadius: "16px",
  padding: "16px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 10px 22px rgba(15,23,42,0.06)"
};

const panelHeader = {
  marginBottom: "12px"
};

const panelTitleWrap = {
  display: "flex",
  alignItems: "center",
  gap: "10px"
};

const panelIcon = {
  width: "34px",
  height: "34px",
  borderRadius: "10px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "16px"
};

const panelTitle = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 700,
  color: theme.text
};

const panelSubtitle = {
  margin: "4px 0 0 0",
  fontSize: "12px",
  color: theme.textMuted
};

const chartArea = {
  height: "300px"
};

const emptyState = {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.textMuted,
  fontSize: "12px",
  textAlign: "center"
};

const tablePanel = {
  background: theme.surface,
  borderRadius: "16px",
  padding: "16px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 10px 22px rgba(15,23,42,0.06)"
};

const tableHeader = {
  marginBottom: "12px"
};

const tableTitle = {
  margin: 0,
  fontSize: "17px",
  fontWeight: 700,
  color: theme.text
};

const tableSubtitle = {
  margin: "4px 0 0 0",
  fontSize: "12px",
  color: theme.textMuted
};

const tableWrapOuter = {
  overflowX: "auto"
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0
};

const tableHeadRow = {
  background: "#F1F5F9"
};

const th = {
  padding: "11px 12px",
  textAlign: "left",
  borderBottom: "1px solid #E2E8F0",
  color: theme.text,
  fontWeight: 700,
  fontSize: "12px",
  position: "sticky",
  top: 0
};

const td = {
  padding: "11px 12px",
  borderBottom: "1px solid #F1F5F9",
  color: theme.text,
  fontSize: "12px"
};

export default EmployeeDetails;